import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

interface RequestAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedFeature?: string;
}

const USE_CASES = [
  { value: 'SOC2', label: 'SOC 2 Audit' },
  { value: 'ISO27001', label: 'ISO 27001 Audit' },
  { value: 'CustomerSecurityQuestionnaire', label: 'Customer Security Questionnaire' },
  { value: 'InternalAudit', label: 'Internal Audit' },
  { value: 'Other', label: 'Other' },
] as const;

const URGENCIES = [
  { value: 'ThisMonth', label: 'This Month' },
  { value: 'NextQuarter', label: 'Next Quarter' },
  { value: 'Later', label: 'Later / Exploring' },
] as const;

const COMPANY_SIZES = [
  { value: 'Solo', label: 'Solo (1 person)' },
  { value: 'Small', label: 'Small Team (2-10)' },
  { value: 'Medium', label: 'Medium (11-50)' },
  { value: 'MediumLarge', label: 'Medium-Large (51-200)' },
  { value: 'Large', label: 'Large (201-1000)' },
  { value: 'Enterprise', label: 'Enterprise (1000+)' },
] as const;

const AUDIT_TIMELINES = [
  { value: 'ThisMonth', label: 'This Month' },
  { value: 'Within3Months', label: 'Within 3 Months' },
  { value: 'Within6Months', label: 'Within 6 Months' },
  { value: 'LaterOrUnknown', label: 'Later / Unknown' },
] as const;

export default function RequestAccessModal({
  isOpen,
  onClose,
  preselectedFeature,
}: RequestAccessModalProps) {
  const [requestedFeature, setRequestedFeature] = useState(preselectedFeature || '');
  const [useCase, setUseCase] = useState('');
  const [urgency, setUrgency] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [auditTimeline, setAuditTimeline] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!requestedFeature.trim() || !useCase || !urgency || !companySize) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/v1/access-requests', {
        requestedFeature: requestedFeature.trim(),
        useCase,
        urgency,
        companySize,
        auditTimeline: auditTimeline || null,
        notes: notes.trim() || null,
      });

      setIsSubmitted(true);
      toast.success('Access request submitted successfully!');
    } catch (error) {
      console.error('Access request submission error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form state when closing
    setRequestedFeature(preselectedFeature || '');
    setUseCase('');
    setUrgency('');
    setCompanySize('');
    setAuditTimeline('');
    setNotes('');
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" onClick={handleClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {isSubmitted ? 'Request Received' : 'Request Access'}
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {isSubmitted ? (
              <div className="text-center py-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-4">
                  Thank you for your interest! We'll review your request and get back to you soon.
                </p>
                <button
                  onClick={handleClose}
                  className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-600 text-sm mb-4">
                  Tell us what you need and we'll work with you to find the right solution.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Requested Feature */}
                  <div>
                    <label htmlFor="requested-feature" className="block text-sm font-medium text-gray-700 mb-1">
                      Feature or Capability <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="requested-feature"
                      value={requestedFeature}
                      onChange={(e) => setRequestedFeature(e.target.value)}
                      placeholder="e.g., AI Metrics & Reporting"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isSubmitting || !!preselectedFeature}
                      required
                      maxLength={200}
                    />
                  </div>

                  {/* Use Case */}
                  <div>
                    <label htmlFor="use-case" className="block text-sm font-medium text-gray-700 mb-1">
                      Use Case <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="use-case"
                      value={useCase}
                      onChange={(e) => setUseCase(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select a use case...</option>
                      {USE_CASES.map((uc) => (
                        <option key={uc.value} value={uc.value}>
                          {uc.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Urgency */}
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                      Timeline <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="urgency"
                      value={urgency}
                      onChange={(e) => setUrgency(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select urgency...</option>
                      {URGENCIES.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Company Size */}
                  <div>
                    <label htmlFor="company-size" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Size <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="company-size"
                      value={companySize}
                      onChange={(e) => setCompanySize(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select company size...</option>
                      {COMPANY_SIZES.map((cs) => (
                        <option key={cs.value} value={cs.value}>
                          {cs.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Audit Timeline (Optional) */}
                  <div>
                    <label htmlFor="audit-timeline" className="block text-sm font-medium text-gray-700 mb-1">
                      Audit Timeline (optional)
                    </label>
                    <select
                      id="audit-timeline"
                      value={auditTimeline}
                      onChange={(e) => setAuditTimeline(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isSubmitting}
                    >
                      <option value="">Select if applicable...</option>
                      {AUDIT_TIMELINES.map((at) => (
                        <option key={at.value} value={at.value}>
                          {at.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes (optional)
                    </label>
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Tell us more about your needs..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                      rows={3}
                      disabled={isSubmitting}
                      maxLength={2000}
                    />
                    <p className="text-xs text-gray-500 mt-1">{notes.length}/2000 characters</p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      disabled={isSubmitting || !requestedFeature.trim() || !useCase || !urgency || !companySize}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
